import React from 'react';
import { Shield, ArrowLeft } from 'lucide-react';

const PrivacyPolicyPage = () => {
    return (
        <div className="min-h-screen bg-slate-50 text-slate-800 p-6 md:p-12 font-sans">
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12">

                <div className="flex items-center justify-between mb-8 pb-6 border-b border-slate-100">
                    <div className="flex items-center space-x-3">
                        <div className="bg-blue-100 p-3 rounded-lg text-blue-600">
                            <Shield className="w-8 h-8" />
                        </div>
                        <h1 className="text-3xl font-bold text-slate-900">Política de Privacidade</h1>
                    </div>
                    <a href="/" className="flex items-center text-slate-500 hover:text-blue-600 transition-colors">
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        <span>Voltar</span>
                    </a>
                </div>

                <div className="space-y-8 text-slate-600 leading-relaxed">

                    <section>
                        <p className="text-sm text-slate-400 mb-4">Última atualização: {new Date().toLocaleDateString('pt-BR')}</p>
                        <p>
                            A <strong>CONNECT10 INTERNET LTDA</strong> ("nós", "nosso" ou "empresa") leva a sua privacidade a sério.
                            Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos as suas
                            informações pessoais ao utilizar o nosso aplicativo corporativo.
                        </p>
                        <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-500 text-blue-800 rounded-r-lg">
                            <strong>Aviso Importante:</strong> Este aplicativo é de uso <strong>exclusivo e restrito</strong> aos colaboradores, parceiros e pessoas autorizadas da CONNECT10 INTERNET LTDA. Ele não é destinado ao público em geral.
                        </div>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-slate-800 mb-3">1. Informações que Coletamos</h2>
                        <p className="mb-2">Como ferramenta de gestão e operação interna, podemos coletar os seguintes dados:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li><strong>Dados de Identificação e Contato:</strong> Nome, endereço de e-mail corporativo, número de telefone e cargo.</li>
                            <li><strong>Dados de Autenticação:</strong> Credenciais de acesso ao sistema (ex.: login via Google Workspace ou e-mail/senha).</li>
                            <li><strong>Dados de Localização:</strong> Em funcionalidades específicas (como marcação de coordenadas, registro de infraestrutura ou rotas), podemos coletar a localização (GPS) do dispositivo, sempre mediante solicitação de permissão no próprio dispositivo.</li>
                            <li><strong>Dados de Uso e Dispositivo:</strong> Informações sobre como você interage com o aplicativo, modelo do dispositivo, sistema operacional e logs de erro para fins de diagnóstico e segurança.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-slate-800 mb-3">2. Como Usamos Suas Informações</h2>
                        <p className="mb-2">Os dados coletados são utilizados exclusivamente para as seguintes finalidades legítimas relacionadas às suas atividades profissionais:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Permitir o acesso e o funcionamento adequado do aplicativo e suas ferramentas.</li>
                            <li>Gerenciar fluxos de trabalho, registro de pendências, projetos e monitoramento de rede.</li>
                            <li>Otimizar o deslocamento e a localização de infraestrutura da empresa.</li>
                            <li>Garantir a segurança da informação e prevenir acessos não autorizados.</li>
                            <li>Cumprir obrigações legais, regulatórias ou ordens judiciais.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-slate-800 mb-3">3. Compartilhamento de Dados</h2>
                        <p>
                            Suas informações pessoais <strong>não</strong> são vendidas, alugadas ou compartilhadas com terceiros para fins de marketing ou publicidade.
                            O compartilhamento ocorre estritamente nos seguintes casos:
                        </p>
                        <ul className="list-disc pl-6 space-y-2 mt-2">
                            <li><strong>Fornecedores de Tecnologia:</strong> Utilizamos provedores de serviços de computação em nuvem (como Firebase e Google Cloud) para hospedar a aplicação e armazenar dados com segurança.</li>
                            <li><strong>Requisitos Legais:</strong> Quando exigido por lei ou para responder a processos legais.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-slate-800 mb-3">4. Retenção dos Dados</h2>
                        <p>
                            Manteremos suas informações pessoais pelo tempo necessário para cumprir as finalidades para as quais foram coletadas,
                            que geralmente corresponde ao período em que você possuir vínculo com a empresa. Após o término do vínculo,
                            os dados poderão ser retidos pelo tempo exigido por obrigações legais ou trabalhistas.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-slate-800 mb-3">5. Segurança dos Dados</h2>
                        <p>
                            Implementamos medidas técnicas e organizacionais adequadas para proteger seus dados pessoais contra acesso,
                            alteração, divulgação ou destruição não autorizada. Nossos servidores utilizam criptografia e controles de acesso
                            rigorosos.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-slate-800 mb-3">6. Seus Direitos (LGPD) e Exclusão de Conta</h2>
                        <p className="mb-2">Em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018), você tem direito a:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Confirmar a existência de tratamento de dados.</li>
                            <li>Acessar os seus dados.</li>
                            <li>Corrigir dados incompletos, inexatos ou desatualizados.</li>
                            <li>Solicitar a anonimização, bloqueio ou eliminação de dados desnecessários ou excessivos.</li>
                        </ul>
                        <p className="mt-2">
                            Para exercer seus direitos, por favor, entre em contato com o departamento de Recursos Humanos ou de Tecnologia da Informação da empresa.
                        </p>
                        <div className="mt-4 p-4 bg-slate-100 rounded-lg border border-slate-200">
                            <strong>Remoção de Conta:</strong> Para solicitar a remoção de sua conta do app "Projetos de Infra", entre em contato com o Departamento de Engenharia ou solicite através do e-mail <a href="mailto:redefibra@connect10.com.br" className="text-blue-600 hover:underline">redefibra@connect10.com.br</a>.
                        </div>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-slate-800 mb-3">7. Alterações nesta Política</h2>
                        <p>
                            Podemos atualizar esta Política de Privacidade periodicamente para refletir mudanças em nossas práticas ou por razões operacionais e legais.
                            Notificaremos sobre mudanças significativas através dos canais de comunicação interna ou diretamente no aplicativo.
                        </p>
                    </section>

                    <section className="bg-slate-50 p-6 rounded-xl border border-slate-200 mt-8">
                        <h2 className="text-xl font-bold text-slate-800 mb-2">8. Contato</h2>
                        <p>
                            Se você tiver dúvidas sobre esta Política de Privacidade ou sobre o tratamento de seus dados,
                            entre em contato com os administradores do sistema interno da CONNECT10.
                        </p>
                    </section>

                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicyPage;
